import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Tabs,
  Tab,
  Chip,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  Checkbox,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import SortIcon from "@mui/icons-material/Sort";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import Loader from "../../Loader";
import "../../Styles/table.css";
import { GetHouseholds } from "../../redux/Actions/userStatusService";
import AddHouseholdMemberModal from "../../Modals/AddHouseholdMemberModal";
import EditMemberPermissionsModal from "../../Modals/EditMemberPermissionsModal";
import RemoveMemberModal from "../../Modals/RemoveMemberModal";
import RenameHouseholdModal from "../../Modals/RenameHouseholdModal";
import ChangeOwnerModal from "../../Modals/ChangeOwnerModal";

const HouseholdInvestorsTable = ({ onDetailsChange, onShowSnackbar }) => {
  const dispatch = useDispatch();
  const { households: householdData = [], householdsLoading: loading } =
    useSelector((state) => state.userStatus);

  // List view state
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(7);

  // Details view state
  const [selectedHousehold, setSelectedHousehold] = useState(null);
  const [detailsTab, setDetailsTab] = useState(1); // 0: Custodian-wise, 1: Household-wise
  const [memberSearchTerm, setMemberSearchTerm] = useState("");
  const [detailsPage, setDetailsPage] = useState(0);
  const [detailsRowsPerPage, setDetailsRowsPerPage] = useState(6);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const [viewPermissionsOpen, setViewPermissionsOpen] = useState(false);
  const [selectedMemberForPermissions, setSelectedMemberForPermissions] = useState(null);
  const [editPermissionsOpen, setEditPermissionsOpen] = useState(false);
  const [selectedMemberForEdit, setSelectedMemberForEdit] = useState(null);
  const [removeMemberOpen, setRemoveMemberOpen] = useState(false);
  const [selectedMemberForRemove, setSelectedMemberForRemove] = useState(null);
  const [renameHouseholdOpen, setRenameHouseholdOpen] = useState(false);
  const [changeOwnerOpen, setChangeOwnerOpen] = useState(false);
  const [pendingRemoveAfterOwnerChange, setPendingRemoveAfterOwnerChange] = useState(false);

  // Sort and Filter state for household-wise investors tab
  const [sortField, setSortField] = useState(null); // 'name', 'accountType', 'status', 'permissionType'
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc'
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [sortAnchorEl, setSortAnchorEl] = useState(null);
  const [filters, setFilters] = useState({
    accountType: [], // ['Individual', 'Corporate', 'Trust']
    status: [], // ['ACCEPTED', 'PENDING']
    permissionType: [], // Array of permission group names
  });

  useEffect(() => {
    dispatch(GetHouseholds());
  }, [dispatch]);

  // Sync selectedHousehold with updated Redux data when householdData changes
  useEffect(() => {
    if (selectedHousehold && householdData.length > 0) {
      const updatedHousehold = householdData.find(
        (h) => h.id === selectedHousehold.id
      );
      if (updatedHousehold) {
        setSelectedHousehold(updatedHousehold);
      }
    }
  }, [householdData, selectedHousehold?.id]);

  // Helper function to refresh household data after any operation
  const refreshHouseholdData = async () => {
    await dispatch(GetHouseholds());
    // The useEffect above will automatically sync selectedHousehold with updated data
  };

  const handleView = (household) => {
    setSelectedHousehold(household);
    setDetailsTab(1);
    setMemberSearchTerm("");
    setDetailsPage(0);
    setMenuAnchorEl(null);
    // Reset filters and sort when viewing a new household
    setFilters({
      accountType: [],
      status: [],
      permissionType: [],
    });
    setSortField(null);
    setSortDirection('asc');
    if (onDetailsChange) onDetailsChange(true);
  };

  const handleCloseDetails = () => {
    setSelectedHousehold(null);
    setMenuAnchorEl(null);
    // Reset filters and sort when closing details
    setFilters({
      accountType: [],
      status: [],
      permissionType: [],
    });
    setSortField(null);
    setSortDirection('asc');
    if (onDetailsChange) onDetailsChange(false);
  };

  const handleDetailsTabChange = (event, newValue) => {
    setDetailsTab(newValue);
  };

  const handleDetailsMenuOpen = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleDetailsMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const getOwnerInfo = (household) => {
    const members = Array.isArray(household?.members)
      ? household.members
      : [];

    const ownerMember = members.find((member) => member?.isOwner);

    if (!ownerMember) {
      return {
        ownerName:
          household?.ownerName ||
          household?.owner?.name ||
          household?.ownerFullName ||
          "N/A",
        ownerEmail: household?.ownerEmail || household?.owner?.email || "",
      };
    }

    const userInfo = ownerMember.userInfo || {};
    const user = ownerMember.userRole?.user || {};
    const userDetails = user.userInfo || {};

    const fullNameFromUserDetails = [userDetails.firstName, userDetails.lastName]
      .filter(Boolean)
      .join(" ");

    const name =
      userInfo.name ||
      fullNameFromUserDetails ||
      household?.ownerName ||
      household?.owner?.name ||
      household?.ownerFullName ||
      "N/A";

    const email =
      userInfo.email ||
      user.email ||
      household?.ownerEmail ||
      household?.owner?.email ||
      "";

    return { ownerName: name, ownerEmail: email };
  };

  const getAccountTypeLabel = (member) => {
    const rawType = member?.userRole?.investorType || "";
    const upper = String(rawType).toUpperCase();
    if (upper === "INDIVIDUAL") return "Individual";
    if (upper === "CORPORATE") return "Corporate";
    if (upper === "TRUST") return "Trust";
    return "-";
  };

  const getStatusConfig = (status) => {
    const upper = String(status || "").toUpperCase();
    if (upper === "ACCEPTED") {
      return { label: "Active", bg: "#ECFDF3", color: "#15803D" };
    }
    if (upper === "PENDING") {
      return { label: "Pending Invite", bg: "#FEF3C7", color: "#B45309" };
    }
    return { label: upper || "-", bg: "#E5E7EB", color: "#374151" };
  };

  // List view data
  const filteredHouseholds = (householdData || []).filter((item) => {
    const name = item?.householdName || "";
    return name.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const paginatedHouseholds =
    rowsPerPage > 0
      ? filteredHouseholds.slice(
          page * rowsPerPage,
          page * rowsPerPage + rowsPerPage
        )
      : filteredHouseholds;

  // Details view data
  const currentMembers = Array.isArray(selectedHousehold?.members)
    ? selectedHousehold.members
    : [];

  // Apply search filter
  const searchFilteredMembers = currentMembers.filter((member) => {
    const info = member?.userInfo || {};
    const name = info.name || "";
    const email = info.email || "";
    const haystack = `${name} ${email}`.toLowerCase();
    return haystack.includes(memberSearchTerm.toLowerCase());
  });

  // Apply additional filters
  const filteredMembers = searchFilteredMembers.filter((member) => {
    // Account Type filter
    if (filters.accountType.length > 0) {
      const accountType = getAccountTypeLabel(member);
      if (!filters.accountType.includes(accountType)) {
        return false;
      }
    }

    // Status filter
    if (filters.status.length > 0) {
      const memberStatus = String(member.status || "").toUpperCase();
      const filterStatuses = filters.status.map(s => String(s).toUpperCase());
      if (!filterStatuses.includes(memberStatus)) {
        return false;
      }
    }

    // Permission Type filter
    if (filters.permissionType.length > 0) {
      const permissionGroupName = member.permissionGroup?.groupName || "";
      const hasNoPermission = permissionGroupName === "";
      
      if (hasNoPermission) {
        // Member has no permission group
        if (!filters.permissionType.includes("No Permission")) {
          return false;
        }
      } else {
        // Member has a permission group
        if (!filters.permissionType.includes(permissionGroupName)) {
          return false;
        }
      }
    }

    return true;
  });

  // Apply sorting
  const sortedAndFilteredMembers = [...filteredMembers].sort((a, b) => {
    if (!sortField) return 0;

    let aValue, bValue;

    switch (sortField) {
      case 'name':
        aValue = (a?.userInfo?.name || "").toLowerCase();
        bValue = (b?.userInfo?.name || "").toLowerCase();
        break;
      case 'accountType':
        aValue = getAccountTypeLabel(a);
        bValue = getAccountTypeLabel(b);
        break;
      case 'status':
        aValue = String(a?.status || "").toUpperCase();
        bValue = String(b?.status || "").toUpperCase();
        break;
      case 'permissionType':
        aValue = (a?.permissionGroup?.groupName || "").toLowerCase();
        bValue = (b?.permissionGroup?.groupName || "").toLowerCase();
        break;
      default:
        return 0;
    }

    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const paginatedMembers =
    detailsRowsPerPage > 0
      ? sortedAndFilteredMembers.slice(
          detailsPage * detailsRowsPerPage,
          detailsPage * detailsRowsPerPage + detailsRowsPerPage
        )
      : sortedAndFilteredMembers;

  // Detail view screen
  if (selectedHousehold) {
    return (
      <Box sx={{ mt: 3 }}>
        {/* Header (no card) */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 3,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <IconButton
              onClick={handleCloseDetails}
              size="small"
              sx={{ mr: 0.5 }}
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
            <Box>
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, color: "#111827" }}
              >
                {selectedHousehold.householdName || "Household"}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "#6B7280", mt: 0.5 }}
              >
                {selectedHousehold.description ||
                  "Manage your household and view details"}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Button
              variant="contained"
              sx={{
                backgroundColor: "#2563EB",
                color: "#fff",
                textTransform: "none",
                fontSize: "14px",
                fontWeight: 600,
                px: 3,
                py: 1,
                borderRadius: "4px",
                "&:hover": {
                  backgroundColor: "#1D4ED8",
                },
              }}
              onClick={() => setAddMemberOpen(true)}
            >
              Add Member
            </Button>
            <IconButton
              onClick={handleDetailsMenuOpen}
              size="small"
              sx={{ color: "#4B5563" }}
            >
              <MoreVertIcon />
            </IconButton>
            <Menu
              anchorEl={menuAnchorEl}
              open={Boolean(menuAnchorEl)}
              onClose={handleDetailsMenuClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
            >
              <MenuItem
                onClick={() => {
                  handleDetailsMenuClose();
                  setRenameHouseholdOpen(true);
                }}
              >
                Rename Household
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleDetailsMenuClose();
                  setChangeOwnerOpen(true);
                }}
              >
                Change Owner
              </MenuItem>
            </Menu>
          </Box>
        </Box>

        {/* Tabs card */}
        <Paper
          elevation={0}
          sx={{
            mt: 2,
            p: 2,
            borderRadius: 2,
            boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.05)",
          }}
        >
          <Box sx={{ borderBottom: 1, borderColor: "divider", px: 3 }}>
            <Tabs
              value={detailsTab}
              onChange={handleDetailsTabChange}
              sx={{
                "& .MuiTab-root": {
                  textTransform: "none",
                  fontSize: "16px",
                  fontWeight: 500,
                  color: "#6B7280",
                  minWidth: "auto",
                  px: 0,
                  mr: 4,
                  py: 2,
                  "&.Mui-selected": {
                    color: "#2563EB",
                    fontWeight: 600,
                  },
                },
                "& .MuiTabs-indicator": {
                  backgroundColor: "#2563EB",
                  height: 3,
                },
              }}
            >
              <Tab label="Custodian-wise Investors" />
              <Tab label="Household-wise Investors" />
            </Tabs>
          </Box>
        </Paper>

        {/* Table card */}
        <Paper
          elevation={0}
          sx={{
            mt: 2,
            borderRadius: 2,
            boxShadow:
              "0 1px 2px rgba(15, 23, 42, 0.04), 0 1px 3px rgba(15, 23, 42, 0.08)",
          }}
        >
          <Box
            sx={{
              px: 3,
              pt: 2,
              pb: 3,
            }}
          >
            {detailsTab === 0 && (
              <Box
                sx={{
                  py: 6,
                  textAlign: "center",
                  color: "#6B7280",
                  fontSize: "14px",
                }}
              >
                Custodian-wise investors view is not implemented yet.
              </Box>
            )}

            {detailsTab === 1 && (
              <>
                {/* Top toolbar: Search + Filter/Sort */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    px: 0.5,
                    pt: 1,
                    pb: 1.5,
                    gap: 2,
                  }}
                >
                  <TextField
                    value={memberSearchTerm}
                    onChange={(e) => setMemberSearchTerm(e.target.value)}
                    placeholder="Search"
                    size="small"
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: "#9CA3AF" }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      maxWidth: 360,
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                      },
                    }}
                  />

                  <Box sx={{ display: "flex", gap: 1.5 }}>
                    <Button
                      variant="text"
                      startIcon={<FilterListIcon />}
                      onClick={(e) => setFilterAnchorEl(e.currentTarget)}
                      sx={{
                        textTransform: "none",
                        color: "#4B5563",
                        fontWeight: 500,
                        backgroundColor: 
                          filters.accountType.length > 0 || 
                          filters.status.length > 0 || 
                          filters.permissionType.length > 0
                            ? "#EFF6FF"
                            : "transparent",
                        "&:hover": {
                          backgroundColor: "#EFF6FF",
                        },
                      }}
                    >
                      Filter
                      {(filters.accountType.length > 0 || 
                        filters.status.length > 0 || 
                        filters.permissionType.length > 0) && (
                        <Chip
                          label={
                            filters.accountType.length +
                            filters.status.length +
                            filters.permissionType.length
                          }
                          size="small"
                          sx={{
                            ml: 1,
                            height: 20,
                            fontSize: "11px",
                            backgroundColor: "#2563EB",
                            color: "#fff",
                          }}
                        />
                      )}
                    </Button>
                    <Button
                      variant="text"
                      startIcon={<SortIcon />}
                      onClick={(e) => setSortAnchorEl(e.currentTarget)}
                      sx={{
                        textTransform: "none",
                        color: "#4B5563",
                        fontWeight: 500,
                        backgroundColor: sortField ? "#EFF6FF" : "transparent",
                        "&:hover": {
                          backgroundColor: "#EFF6FF",
                        },
                      }}
                    >
                      Sort
                    </Button>
                  </Box>

                  {/* Filter Menu */}
                  <Menu
                    anchorEl={filterAnchorEl}
                    open={Boolean(filterAnchorEl)}
                    onClose={() => setFilterAnchorEl(null)}
                    onClick={(e) => {
                      // Prevent menu from closing when clicking inside
                      if (e.target.closest('.MuiCheckbox-root') || e.target.closest('.MuiMenuItem-root')) {
                        e.stopPropagation();
                      }
                    }}
                    PaperProps={{
                      sx: {
                        mt: 1,
                        minWidth: 280,
                        borderRadius: 2,
                        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                      },
                    }}
                  >
                    <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid #E5E7EB" }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Typography sx={{ fontWeight: 600, fontSize: "14px", color: "#111827" }}>
                          Filter By
                        </Typography>
                        {(filters.accountType.length > 0 || 
                          filters.status.length > 0 || 
                          filters.permissionType.length > 0) && (
                          <Button
                            size="small"
                            onClick={() => {
                              setFilters({
                                accountType: [],
                                status: [],
                                permissionType: [],
                              });
                              setDetailsPage(0);
                            }}
                            sx={{
                              textTransform: "none",
                              fontSize: "12px",
                              color: "#2563EB",
                              fontWeight: 500,
                            }}
                          >
                            Clear All
                          </Button>
                        )}
                      </Box>
                    </Box>

                    {/* Account Type Filter */}
                    <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid #E5E7EB" }}>
                      <Typography sx={{ fontWeight: 600, fontSize: "12px", color: "#6B7280", mb: 1 }}>
                        Account Type
                      </Typography>
                      {['Individual', 'Corporate', 'Trust'].map((type) => (
                        <MenuItem
                          key={type}
                          onClick={(e) => {
                            // Let the checkbox handle the click
                            e.preventDefault();
                          }}
                          sx={{ py: 0.5, px: 1 }}
                        >
                          <Checkbox
                            checked={filters.accountType.includes(type)}
                            size="small"
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => {
                              e.stopPropagation();
                              setFilters((prev) => ({
                                ...prev,
                                accountType: prev.accountType.includes(type)
                                  ? prev.accountType.filter((t) => t !== type)
                                  : [...prev.accountType, type],
                              }));
                              setDetailsPage(0);
                            }}
                            sx={{
                              color: "#D1D5DB",
                              "&.Mui-checked": {
                                color: "#2563EB",
                              },
                            }}
                          />
                          <Typography sx={{ fontSize: "13px", ml: 1 }}>
                            {type}
                          </Typography>
                        </MenuItem>
                      ))}
                    </Box>

                    {/* Status Filter */}
                    <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid #E5E7EB" }}>
                      <Typography sx={{ fontWeight: 600, fontSize: "12px", color: "#6B7280", mb: 1 }}>
                        Status
                      </Typography>
                      {['ACCEPTED', 'PENDING'].map((status) => (
                        <MenuItem
                          key={status}
                          onClick={(e) => {
                            // Let the checkbox handle the click
                            e.preventDefault();
                          }}
                          sx={{ py: 0.5, px: 1 }}
                        >
                          <Checkbox
                            checked={filters.status.includes(status)}
                            size="small"
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => {
                              e.stopPropagation();
                              setFilters((prev) => ({
                                ...prev,
                                status: prev.status.includes(status)
                                  ? prev.status.filter((s) => s !== status)
                                  : [...prev.status, status],
                              }));
                              setDetailsPage(0);
                            }}
                            sx={{
                              color: "#D1D5DB",
                              "&.Mui-checked": {
                                color: "#2563EB",
                              },
                            }}
                          />
                          <Typography sx={{ fontSize: "13px", ml: 1 }}>
                            {status === 'ACCEPTED' ? 'Active' : 'Pending Invite'}
                          </Typography>
                        </MenuItem>
                      ))}
                    </Box>

                    {/* Permission Type Filter */}
                    <Box sx={{ px: 2, py: 1.5 }}>
                      <Typography sx={{ fontWeight: 600, fontSize: "12px", color: "#6B7280", mb: 1 }}>
                        Permission Type
                      </Typography>
                      {(() => {
                        const permissionGroups = new Set();
                        currentMembers.forEach((member) => {
                          const groupName = member.permissionGroup?.groupName;
                          if (groupName) {
                            permissionGroups.add(groupName);
                          }
                        });
                        const permissionList = Array.from(permissionGroups).sort();
                        
                        return (
                          <>
                            {permissionList.map((permission) => (
                              <MenuItem
                                key={permission}
                                onClick={(e) => {
                                  // Let the checkbox handle the click
                                  e.preventDefault();
                                }}
                                sx={{ py: 0.5, px: 1 }}
                              >
                                <Checkbox
                                  checked={filters.permissionType.includes(permission)}
                                  size="small"
                                  onClick={(e) => e.stopPropagation()}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    setFilters((prev) => ({
                                      ...prev,
                                      permissionType: prev.permissionType.includes(permission)
                                        ? prev.permissionType.filter((p) => p !== permission)
                                        : [...prev.permissionType, permission],
                                    }));
                                    setDetailsPage(0);
                                  }}
                                  sx={{
                                    color: "#D1D5DB",
                                    "&.Mui-checked": {
                                      color: "#2563EB",
                                    },
                                  }}
                                />
                                <Typography sx={{ fontSize: "13px", ml: 1 }}>
                                  {permission}
                                </Typography>
                              </MenuItem>
                            ))}
                            <MenuItem
                              onClick={(e) => {
                                // Let the checkbox handle the click
                                e.preventDefault();
                              }}
                              sx={{ py: 0.5, px: 1 }}
                            >
                              <Checkbox
                                checked={filters.permissionType.includes("No Permission")}
                                size="small"
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  setFilters((prev) => ({
                                    ...prev,
                                    permissionType: prev.permissionType.includes("No Permission")
                                      ? prev.permissionType.filter((p) => p !== "No Permission")
                                      : [...prev.permissionType, "No Permission"],
                                  }));
                                  setDetailsPage(0);
                                }}
                                sx={{
                                  color: "#D1D5DB",
                                  "&.Mui-checked": {
                                    color: "#2563EB",
                                  },
                                }}
                              />
                              <Typography sx={{ fontSize: "13px", ml: 1 }}>
                                No Permission
                              </Typography>
                            </MenuItem>
                          </>
                        );
                      })()}
                    </Box>
                  </Menu>

                  {/* Sort Menu */}
                  <Menu
                    anchorEl={sortAnchorEl}
                    open={Boolean(sortAnchorEl)}
                    onClose={() => setSortAnchorEl(null)}
                    PaperProps={{
                      sx: {
                        mt: 1,
                        minWidth: 200,
                        borderRadius: 2,
                        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                      },
                    }}
                  >
                    <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid #E5E7EB" }}>
                      <Typography sx={{ fontWeight: 600, fontSize: "14px", color: "#111827" }}>
                        Sort By
                      </Typography>
                    </Box>
                    {[
                      { value: 'name', label: 'Member Name' },
                      { value: 'accountType', label: 'Account Type' },
                      { value: 'status', label: 'Status' },
                      { value: 'permissionType', label: 'Permission Type' },
                    ].map((option) => (
                      <MenuItem
                        key={option.value}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (sortField === option.value) {
                            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                          } else {
                            setSortField(option.value);
                            setSortDirection('asc');
                          }
                          setDetailsPage(0);
                          // Don't close menu immediately to allow toggling direction
                        }}
                        sx={{ py: 1, px: 2 }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
                          <Typography sx={{ fontSize: "13px" }}>
                            {option.label}
                          </Typography>
                          {sortField === option.value && (
                            <Typography sx={{ fontSize: "12px", color: "#2563EB", ml: 2 }}>
                              {sortDirection === 'asc' ? '↑' : '↓'}
                            </Typography>
                          )}
                        </Box>
                      </MenuItem>
                    ))}
                    {sortField && (
                      <>
                        <Box sx={{ borderTop: "1px solid #E5E7EB", mt: 0.5 }} />
                        <MenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            setSortField(null);
                            setSortDirection('asc');
                            setDetailsPage(0);
                            setSortAnchorEl(null);
                          }}
                          sx={{ py: 1, px: 2 }}
                        >
                          <Typography sx={{ fontSize: "13px", color: "#DC2626" }}>
                            Clear Sort
                          </Typography>
                        </MenuItem>
                      </>
                    )}
                  </Menu>
                </Box>

                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            color: "#6B7280",
                            fontSize: "12px",
                          }}
                        >
                          Members
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            color: "#6B7280",
                            fontSize: "12px",
                          }}
                        >
                          Account Type
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            color: "#6B7280",
                            fontSize: "12px",
                          }}
                        >
                          Status
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            color: "#6B7280",
                            fontSize: "12px",
                          }}
                        >
                          Permission Type
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            fontWeight: 600,
                            color: "#6B7280",
                            fontSize: "12px",
                          }}
                        >
                          Action
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedMembers.map((member) => {
                        const info = member.userInfo || {};
                        const { label: statusLabel, bg, color } =
                          getStatusConfig(member.status);
                        const accountTypeLabel = getAccountTypeLabel(member);
                        const permissionLabel =
                          member.permissionGroup?.groupName || "-";
                        const isPendingInvite =
                          String(member.status || "").toUpperCase() ===
                          "PENDING";
                        const hasPermissionGroup =
                          !!member.permissionGroup?.groupName;

                        return (
                          <TableRow key={member.id} hover>
                            <TableCell>
                              <Box>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontSize: "14px",
                                      fontWeight: 600,
                                      color: "#111827",
                                    }}
                                  >
                                    {info.name || "N/A"}
                                  </Typography>
                                  {member.isOwner && (
                                    <Chip
                                      label="Owner"
                                      size="small"
                                      sx={{
                                        fontSize: "10px",
                                        height: 20,
                                        borderRadius: "999px",
                                        backgroundColor: "#EEF2FF",
                                        color: "#4F46E5",
                                        fontWeight: 600,
                                      }}
                                    />
                                  )}
                                </Box>
                                {info.email && (
                                  <Typography
                                    sx={{
                                      fontSize: "12px",
                                      color: "#6B7280",
                                      mt: 0.3,
                                    }}
                                  >
                                    {info.email}
                                  </Typography>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={accountTypeLabel}
                                size="small"
                                sx={{
                                  fontSize: "11px",
                                  height: 22,
                                  borderRadius: "999px",
                                  backgroundColor: "#ECFEFF",
                                  color: "#0369A1",
                                  fontWeight: 500,
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={statusLabel}
                                size="small"
                                sx={{
                                  fontSize: "11px",
                                  height: 22,
                                  borderRadius: "999px",
                                  backgroundColor: bg,
                                  color,
                                  fontWeight: 500,
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              {permissionLabel !== "-" ? (
                                <Chip
                                  label={permissionLabel}
                                  size="small"
                                  sx={{
                                    fontSize: "11px",
                                    height: 22,
                                    borderRadius: "999px",
                                    backgroundColor: "#F3F4F6",
                                    color: "#111827",
                                    fontWeight: 500,
                                  }}
                                />
                              ) : (
                                "-"
                              )}
                            </TableCell>
                            <TableCell align="right">
                              {isPendingInvite && !hasPermissionGroup ? (
                                <Button
                                  variant="text"
                                  sx={{
                                    textTransform: "none",
                                    fontWeight: 600,
                                    fontSize: "13px",
                                    color: "#2563EB",
                                  }}
                                 
                                >
                                  Resend invite
                                </Button>
                              ) : (
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "flex-end",
                                    gap: 1,
                                  }}
                                >
                                  <Button
                                    variant="text"
                                    startIcon={
                                      <VisibilityOutlinedIcon
                                        sx={{ fontSize: 18 }}
                                      />
                                    }
                                    sx={{
                                      textTransform: "none",
                                      fontWeight: 600,
                                      fontSize: "13px",
                                      color: "#2563EB",
                                    }}
                                    onClick={() => {
                                      setSelectedMemberForPermissions(member);
                                      setViewPermissionsOpen(true);
                                    }}
                                  >
                                    View Permissions
                                  </Button>
                                  <IconButton
                                    size="small"
                                    sx={{ color: "#2563EB" }}
                                    onClick={() => {
                                      setSelectedMemberForEdit(member);
                                      setEditPermissionsOpen(true);
                                    }}
                                  >
                                    <EditOutlinedIcon sx={{ fontSize: 18 }} />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    sx={{ color: "#2563EB" }}
                                    onClick={() => {
                                      setSelectedMemberForRemove(member);
                                      setRemoveMemberOpen(true);
                                    }}
                                  >
                                    <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </Box>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                <TablePagination
                  component="div"
                  count={sortedAndFilteredMembers.length}
                  page={detailsPage}
                  onPageChange={(event, newPage) => setDetailsPage(newPage)}
                  rowsPerPage={detailsRowsPerPage}
                  onRowsPerPageChange={(event) => {
                    setDetailsRowsPerPage(parseInt(event.target.value, 10));
                    setDetailsPage(0);
                  }}
                  rowsPerPageOptions={[6, 10, 25]}
                  labelRowsPerPage="Rows per page:"
                  sx={{
                    borderTop: "1px solid #E5E7EB",
                    mt: 1,
                  }}
                />
              </>
            )}
          </Box>
        </Paper>

        {/* Add Member wizard modal */}
        <AddHouseholdMemberModal
          open={addMemberOpen}
          onClose={() => setAddMemberOpen(false)}
          household={selectedHousehold}
          onMembersAdded={async (result) => {
            if (result && result.success !== false) {
              // Success case
              if (onShowSnackbar) {
                onShowSnackbar(
                  result.message || "Member(s) added successfully",
                  "success"
                );
              }
              await refreshHouseholdData();
            } else if (result && result.success === false) {
              // Failure case - show error snackbar
              if (onShowSnackbar) {
                onShowSnackbar(
                  result.message || "Failed to add member(s)",
                  "error"
                );
              }
            }
          }}
        />

        {/* Edit Member Permissions Modal */}
        <EditMemberPermissionsModal
          open={editPermissionsOpen}
          onClose={() => {
            setEditPermissionsOpen(false);
            setSelectedMemberForEdit(null);
          }}
          member={selectedMemberForEdit}
          household={selectedHousehold}
          onPermissionsUpdated={async (result) => {
            if (result && result.success !== false) {
              // Success case
              if (onShowSnackbar) {
                onShowSnackbar(
                  result.message || "Member permissions updated successfully",
                  "success"
                );
              }
              await refreshHouseholdData();
              setEditPermissionsOpen(false);
              setSelectedMemberForEdit(null);
            } else if (result && result.success === false) {
              // Failure case - show error snackbar
              if (onShowSnackbar) {
                onShowSnackbar(
                  result.message || "Failed to update member permissions",
                  "error"
                );
              }
            }
          }}
        />

        {/* Remove Member Modal */}
        <RemoveMemberModal
          open={removeMemberOpen}
          onClose={() => {
            setRemoveMemberOpen(false);
            setSelectedMemberForRemove(null);
            setPendingRemoveAfterOwnerChange(false);
          }}
          member={selectedMemberForRemove}
          household={selectedHousehold}
          onMemberRemoved={async (result) => {
            if (result && result.success !== false) {
              // Success case
              if (onShowSnackbar) {
                onShowSnackbar(
                  result.message || "Member removed from household successfully",
                  "success"
                );
              }
              await refreshHouseholdData();
              setRemoveMemberOpen(false);
              setSelectedMemberForRemove(null);
              setPendingRemoveAfterOwnerChange(false);
            } else if (result && result.success === false) {
              // Failure case - show error snackbar
              if (onShowSnackbar) {
                onShowSnackbar(
                  result.message || "Failed to remove member from household",
                  "error"
                );
              }
            }
          }}
          onOwnerChangeRequested={(member) => {
            // Close remove modal and open change owner modal
            setRemoveMemberOpen(false);
            setPendingRemoveAfterOwnerChange(true);
            setChangeOwnerOpen(true);
          }}
        />

        {/* Rename Household Modal */}
        <RenameHouseholdModal
          open={renameHouseholdOpen}
          onClose={() => setRenameHouseholdOpen(false)}
          household={selectedHousehold}
          onHouseholdRenamed={async (result) => {
            if (result && result.success !== false) {
              // Success case
              if (onShowSnackbar) {
                onShowSnackbar(
                  result.message || "Household renamed successfully",
                  "success"
                );
              }
              await refreshHouseholdData();
              setRenameHouseholdOpen(false);
            } else if (result && result.success === false) {
              // Failure case - show error snackbar
              if (onShowSnackbar) {
                onShowSnackbar(
                  result.message || "Failed to rename household",
                  "error"
                );
              }
            }
          }}
        />

        {/* Change Owner Modal */}
        <ChangeOwnerModal
          open={changeOwnerOpen}
          onClose={() => {
            setChangeOwnerOpen(false);
            setPendingRemoveAfterOwnerChange(false);
            // If this was triggered from remove modal, close the remove modal state
            if (pendingRemoveAfterOwnerChange) {
              setRemoveMemberOpen(false);
              setSelectedMemberForRemove(null);
            }
          }}
          household={selectedHousehold}
          onOwnerChanged={async (result) => {
            if (result && result.success !== false) {
              // Success case - owner changed and old owner automatically removed
              if (onShowSnackbar) {
                onShowSnackbar(
                  result.message || "Household owner changed and previous owner removed successfully",
                  "success"
                );
              }
              await refreshHouseholdData();
              setChangeOwnerOpen(false);
              // Close remove modal if it was open
              if (pendingRemoveAfterOwnerChange) {
                setRemoveMemberOpen(false);
                setSelectedMemberForRemove(null);
              }
              setPendingRemoveAfterOwnerChange(false);
            } else if (result && result.success === false) {
              // Failure case - show error snackbar
              if (onShowSnackbar) {
                onShowSnackbar(
                  result.message || "Failed to change household owner",
                  "error"
                );
              }
              setPendingRemoveAfterOwnerChange(false);
            }
          }}
        />

        {/* View Permissions Modal */}
        <Dialog
          open={viewPermissionsOpen}
          onClose={() => {
            setViewPermissionsOpen(false);
            setSelectedMemberForPermissions(null);
          }}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              boxShadow:
                "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
            },
          }}
        >
          <DialogTitle sx={{ pb: 2, pt: 3, px: 3 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
              }}
            >
              <Box>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 700, color: "#000", mb: 0.5 }}
                >
                  View Permissions
                </Typography>
                <Typography variant="body2" sx={{ color: "#6B7280" }}>
                  {selectedMemberForPermissions?.userInfo?.name || "Member"} - Permission Details
                </Typography>
              </Box>
              <IconButton
                onClick={() => {
                  setViewPermissionsOpen(false);
                  setSelectedMemberForPermissions(null);
                }}
                sx={{ color: "#6B7280", mt: -1 }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>

          <DialogContent sx={{ px: 3, pb: 3, pt: 0 }}>
            {selectedMemberForPermissions?.permissionGroup ? (
              <>
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ color: "#6B7280", mb: 1, fontSize: "12px" }}
                  >
                    Permission Group
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, color: "#111827", mb: 1 }}
                  >
                    {selectedMemberForPermissions.permissionGroup.groupName || "N/A"}
                  </Typography>
                  {selectedMemberForPermissions.permissionGroup.description && (
                    <Typography
                      variant="body2"
                      sx={{ color: "#6B7280", mt: 0.5 }}
                    >
                      {selectedMemberForPermissions.permissionGroup.description}
                    </Typography>
                  )}
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, color: "#111827", mb: 2 }}
                  >
                    Assigned Permissions
                  </Typography>
                  {selectedMemberForPermissions.permissionGroup.apiNames &&
                  selectedMemberForPermissions.permissionGroup.apiNames.length > 0 ? (
                    <Box
                      sx={{
                        maxHeight: 400,
                        overflowY: "auto",
                        border: "1px solid #E5E7EB",
                        borderRadius: 2,
                        p: 2,
                      }}
                    >
                      <Grid container spacing={1.5}>
                        {selectedMemberForPermissions.permissionGroup.apiNames.map(
                          (apiName, index) => (
                            <Grid item xs={12} sm={6} key={index}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <CheckCircleIcon
                                  sx={{
                                    color: "#16A34A",
                                    fontSize: 20,
                                    flexShrink: 0,
                                  }}
                                />
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: "#374151",
                                    fontSize: "13px",
                                    wordBreak: "break-word",
                                  }}
                                >
                                  {apiName}
                                </Typography>
                              </Box>
                            </Grid>
                          )
                        )}
                      </Grid>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        py: 4,
                        textAlign: "center",
                        border: "1px solid #E5E7EB",
                        borderRadius: 2,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ color: "#6B7280" }}
                      >
                        No permissions assigned to this member.
                      </Typography>
                    </Box>
                  )}
                </Box>
              </>
            ) : (
              <Box
                sx={{
                  py: 6,
                  textAlign: "center",
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ color: "#6B7280", mb: 1 }}
                >
                  No permission group assigned
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "#9CA3AF" }}
                >
                  This member does not have a permission group assigned yet.
                </Typography>
              </Box>
            )}
          </DialogContent>
        </Dialog>
      </Box>
    );
  }

  // List view screen
  if (loading) {
    return (
      <Box sx={{ mt: 3 }}>
        <Loader />
      </Box>
    );
  }

  if (householdData.length === 0) {
    return (
      <Box
        sx={{
          mt: 3,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          py: 10,
          textAlign: "center",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: "#1F2937",
            mb: 1,
          }}
        >
          No households yet
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: "#6B7280",
          }}
        >
          All households will appear here once they're created.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 3 }}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          boxShadow:
            "0 1px 2px rgba(15, 23, 42, 0.04), 0 1px 3px rgba(15, 23, 42, 0.08)",
        }}
      >
        {/* Top toolbar: Search + Filter/Sort */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 2.5,
            pt: 2,
            pb: 1,
            gap: 2,
          }}
        >
          <TextField
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search"
            size="small"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#9CA3AF" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              maxWidth: 360,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
              },
            }}
          />

          <Box sx={{ display: "flex", gap: 1.5 }}>
            <Button
              variant="text"
              startIcon={<FilterListIcon />}
              sx={{
                textTransform: "none",
                color: "#4B5563",
                fontWeight: 500,
              }}
            >
              Filter
            </Button>
            <Button
              variant="text"
              startIcon={<SortIcon />}
              sx={{
                textTransform: "none",
                color: "#4B5563",
                fontWeight: 500,
              }}
            >
              Sort
            </Button>
          </Box>
        </Box>

        {/* Table */}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    color: "#6B7280",
                    fontSize: "12px",
                  }}
                >
                  Household Name
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: 600,
                    color: "#6B7280",
                    fontSize: "12px",
                  }}
                >
                  Member Count
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: 600,
                    color: "#6B7280",
                    fontSize: "12px",
                  }}
                >
                  Owner
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    fontWeight: 600,
                    color: "#6B7280",
                    fontSize: "12px",
                  }}
                >
                  Action
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedHouseholds.map((item) => {
                const memberCount =
                  item?.memberCount ??
                  item?.numberOfMembers ??
                  (Array.isArray(item?.members) ? item.members.length : 0);

                const { ownerName, ownerEmail } = getOwnerInfo(item);

                return (
                  <TableRow key={item.id} hover>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: "#111827",
                        fontSize: "14px",
                      }}
                    >
                      {item.householdName || "N/A"}
                    </TableCell>
                    <TableCell align="center">
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          px: 1.2,
                          py: 0.3,
                          borderRadius: 999,
                          backgroundColor: "#ECFDF3",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "12px",
                            fontWeight: 600,
                            color: "#15803D",
                          }}
                        >
                          {memberCount ?? "-"}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Box>
                        <Typography
                          sx={{
                            fontSize: "14px",
                            fontWeight: 500,
                            color: "#111827",
                          }}
                        >
                          {ownerName}
                        </Typography>
                        {ownerEmail && (
                          <Typography
                            sx={{
                              fontSize: "12px",
                              color: "#6B7280",
                              mt: 0.3,
                            }}
                          >
                            {ownerEmail}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        variant="text"
                        onClick={() => handleView(item)}
                        sx={{
                          textTransform: "none",
                          fontWeight: 600,
                          fontSize: "13px",
                          color: "#2563EB",
                        }}
                      >
                        View Household
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          component="div"
          count={filteredHouseholds.length}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[7, 10, 25]}
          labelRowsPerPage="Rows per page:"
          sx={{
            borderTop: "1px solid #E5E7EB",
          }}
        />
      </Paper>
    </Box>
  );
};

export default HouseholdInvestorsTable;


